/**
 * マーカーパネル
 */

import React, {
  MouseEvent,
  TouchEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import { Tooltip } from "@mui/material";
import { useToCDNUrl } from "hooks/useCDN";
import { handleClickAction } from "modules/clickActionHandler";
import { addUndo } from "stores/modules/entities.room.histories/slice";
import { useTranslation } from "react-i18next";
import DraggableItem from "containers/DraggableItem";
import { fixSelectionOnDragStop } from "modules/drag";
import { getPositionMouseOrTouch } from "modules/device";

const enableProps = {
  right: true,
  bottom: true,
  bottomRight: true,
};

type MarkerProps = {
  markerId: string;
  roomId: string;
};

const Marker: React.FC<MarkerProps> = (props) => {
  const { markerId, roomId } = props;
  const dispatch = useAppDispatch();
  const toCDNUrl = useToCDNUrl();
  const [t] = useTranslation();
  const [dragging, setDragging] = useState(false);
  const isClicked = React.useRef(false);
  const { marker, isRoleAudience } = useAppSelector((state) => {
    return {
      marker: store.getRoomMarkerById(state, roomId, markerId),
      isRoleAudience: store.getIsRoleAudience(state),
    };
  }, shallowEqual);

  const onContextMenu = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isRoleAudience) {
        return;
      }

      const position = getPositionMouseOrTouch(e);
      dispatch(
        store.appStateMutate((state) => {
          state.openRoomMarkerMenu = true;
          state.openRoomMarkerMenuId = markerId;
          state.roomPointerY = position.y;
          state.roomPointerX = position.x;
        })
      );
    },
    [markerId, isRoleAudience, dispatch]
  );
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (marker.clickAction != null && isClicked.current) {
        return;
      }

      isClicked.current = true;

      setTimeout(() => {
        isClicked.current = false;
      }, 350);

      if (dragging || marker.clickAction == null || isRoleAudience) {
        return;
      }

      handleClickAction(marker.clickAction, dispatch);
    },
    [dragging, marker.clickAction, isRoleAudience, dispatch]
  );
  const onDoubleClick = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (isRoleAudience || marker.clickAction != null) {
        return;
      }

      dispatch(
        store.appStateMutate((state) => {
          state.openRoomMarkerDetail = true;
          state.openRoomMarkerDetailId = markerId;
        })
      );
    },
    [markerId, isRoleAudience, marker.clickAction, dispatch]
  );
  const onUpdate = React.useCallback(
    (position) => {
      dispatch(store.updateRoomMarker(roomId, markerId, position));
      dispatch(
        addUndo({
          kind: "update-marker",
          id: markerId,
          before: marker,
          after: { ...marker, ...position },
        })
      );
    },
    [markerId, roomId, dispatch, marker]
  );

  const onMouseDown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!marker.locked && !isRoleAudience) {
        e.stopPropagation();
        fixSelectionOnDragStop();
      }
    },
    [marker.locked, isRoleAudience]
  );

  const stopPropagation = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!marker.locked && !isRoleAudience) {
        e.stopPropagation();
      }
    },
    [marker.locked, isRoleAudience]
  );

  const keyDownTimer = useRef(0);
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isRoleAudience) {
        return;
      }

      // Skip Paste
      if (e.code === "KeyV" && (e.metaKey || e.ctrlKey)) {
        return;
      }

      // Skip Undo/Redo
      if (
        (e.code === "KeyZ" || e.code === "KeyY") &&
        (e.metaKey || e.ctrlKey)
      ) {
        return;
      }

      if (Date.now() - keyDownTimer.current < 100) {
        keyDownTimer.current = Date.now();
        return;
      }
      keyDownTimer.current = Date.now();
      switch (e.code) {
        // Rotate
        case "KeyR":
          e.preventDefault();
          e.stopPropagation();
          if (markerId && marker) {
            const angleUnit = 45;
            const addAngle = e.shiftKey ? -angleUnit : angleUnit;
            dispatch(
              store.addUndoUpdateMarker(roomId, markerId, {
                angle: marker.angle + addAngle,
              })
            );
          }
          break;
        // Lock
        case "KeyL":
          e.preventDefault();
          e.stopPropagation();
          if (markerId && marker) {
            dispatch(
              store.addUndoUpdateMarker(roomId, markerId, {
                locked: !marker.locked,
              })
            );
          }
          break;
        // Copy to clipboard
        case "KeyC":
          e.preventDefault();
          e.stopPropagation();
          if (markerId && (e.metaKey || e.ctrlKey)) {
            dispatch(store.copyRoomMarkerToClipboard(markerId));
          }
          break;
        // Duplicate
        case "KeyD":
          e.preventDefault();
          e.stopPropagation();
          if (markerId && (e.metaKey || e.ctrlKey)) {
            dispatch(store.cloneRoomMarker(markerId));
          }
          break;
        // Delete（Backspace）
        case "Backspace":
          e.preventDefault();
          e.stopPropagation();
          if (markerId && marker && (e.metaKey || e.ctrlKey)) {
            if (window.confirm(t("本当に削除しますか？"))) {
              dispatch(store.deleteRoomMarker(roomId, markerId));
              dispatch(
                addUndo({
                  kind: "update-marker",
                  id: markerId,
                  before: marker,
                  after: null,
                })
              );
            }
          }
          break;
      }
    },
    [isRoleAudience, markerId, marker, roomId, dispatch, t]
  );

  const hasClickAction = marker.clickAction != null;
  const disableDragging = marker.locked || isRoleAudience;
  const enableResizing = !marker.locked && !marker.freezed && !isRoleAudience;
  const imageUrl = marker.imageUrl || "/blank.gif";

  return (
    <DraggableItem
      initialPosition={{
        x: marker.x,
        y: marker.y,
        width: marker.width,
        height: marker.height,
      }}
      type="marker"
      draggableId={markerId}
      disableDragging={disableDragging}
      enableResizing={enableResizing}
      enableProps={enableProps}
      setDragging={setDragging}
      onUpdate={onUpdate}
      onItemContextMenu={onContextMenu}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchStart={stopPropagation}
      onMouseDown={onMouseDown}
      onItemKeyDown={onKeyDown}
      angle={marker.angle}
      locked={marker.locked}
      style={{
        zIndex: marker.z,
      }}
    >
      <Tooltip title={marker.text || ""} placement="bottom">
        <MarkerContainer
          locked={disableDragging}
          style={{
            cursor: hasClickAction ? "pointer" : undefined,
            transform: `rotate(${marker.angle}deg)`,
          }}
        >
          <img
            src={toCDNUrl(imageUrl)}
            draggable={false}
            width="100%"
            height="100%"
          />
        </MarkerContainer>
      </Tooltip>
    </DraggableItem>
  );
};

const MarkerContainer = styled.div<{ locked: boolean }>`
  width: 100%;
  height: 100%;
  &:hover {
    background: ${(p: { locked: boolean }) =>
      p.locked ? "none" : "rgba(0, 0, 0, 0.2)"};
  }
  img {
    pointer-events: none;
  }
`;

export default React.memo(Marker);
